/**
 * AO-02 "Suggested questions" — fixed, built-in list of example prompts shown on the
 * empty/welcome chat screen so users know how to begin.
 *
 * Each entry has a stable i18n `key` (under the `chat.suggestedQuestions.*` block) and an
 * English `defaultValue` fallback. Only the EN locale needs the key block added; other
 * locales fall back to `defaultValue` automatically until translated.
 *
 * Keep this list short (KISS/YAGNI): ~5-6 examples is enough to orient users.
 */
export interface SuggestedQuestion {
  /** i18n key, e.g. `chat.suggestedQuestions.summarizeDocument` */
  key: string;
  /** English fallback used until the locale provides a translation */
  defaultValue: string;
}

export const SUGGESTED_QUESTIONS: SuggestedQuestion[] = [
  {
    key: 'chat.suggestedQuestions.summarizeDocument',
    defaultValue: 'Summarize the key points of my latest document.',
  },
  {
    key: 'chat.suggestedQuestions.findClause',
    defaultValue: 'Find the clauses about payment terms and cite them.',
  },
  {
    key: 'chat.suggestedQuestions.explainTopic',
    defaultValue: 'Explain how the renewal process works based on my documents.',
  },
  {
    key: 'chat.suggestedQuestions.compareVersions',
    defaultValue: 'What are the main differences between the two latest reports?',
  },
  {
    key: 'chat.suggestedQuestions.listObligations',
    defaultValue: 'List my obligations and deadlines mentioned in the documents.',
  },
  {
    key: 'chat.suggestedQuestions.draftEmail',
    defaultValue: 'Draft a short email summarizing the contract highlights.',
  },
  {
    key: 'chat.suggestedQuestions.identifyRisks',
    defaultValue: 'Identify any risky or unusual clauses in my documents.',
  },
  {
    key: 'chat.suggestedQuestions.findDefinitions',
    defaultValue: 'Find the defined terms and their meanings in my documents.',
  },
  {
    key: 'chat.suggestedQuestions.extractDates',
    defaultValue: 'Extract the key dates, renewals and deadlines from my documents.',
  },
];

/**
 * Empty-state suggestion cards (matches Round-2 mockup): a fixed 2×2 grid where
 * each card shows a bold title + grey subtitle. Clicking a card sends `prompt`
 * through the existing AO-02 send path (see Chat.onSelectSuggestion).
 *
 * `prompt` reuses the same i18n keys/fallbacks as SUGGESTED_QUESTIONS so the
 * actual question text sent stays consistent with the built-in list.
 */
export interface SuggestionCard {
  /** i18n key for the bold card title */
  titleKey: string;
  /** English fallback for the title */
  titleDefault: string;
  /** i18n key for the grey subtitle */
  subtitleKey: string;
  /** English fallback for the subtitle */
  subtitleDefault: string;
  /** i18n key for the question actually sent on click */
  promptKey: string;
  /** English fallback for the sent question */
  promptDefault: string;
}

export const SUGGESTION_CARDS: SuggestionCard[] = [
  {
    titleKey: 'chat.suggestionCards.summariseContract.title',
    titleDefault: 'Summarize a contract',
    subtitleKey: 'chat.suggestionCards.summariseContract.subtitle',
    subtitleDefault: 'Extract key terms, dates and obligations',
    promptKey: 'chat.suggestedQuestions.summarizeDocument',
    promptDefault: SUGGESTED_QUESTIONS[0].defaultValue,
  },
  {
    titleKey: 'chat.suggestionCards.compareDocuments.title',
    titleDefault: 'Compare two documents',
    subtitleKey: 'chat.suggestionCards.compareDocuments.subtitle',
    subtitleDefault: 'Highlight differences across versions',
    promptKey: 'chat.suggestedQuestions.compareVersions',
    promptDefault: SUGGESTED_QUESTIONS[3].defaultValue,
  },
  {
    titleKey: 'chat.suggestionCards.findClause.title',
    titleDefault: 'Find a policy clause',
    subtitleKey: 'chat.suggestionCards.findClause.subtitle',
    subtitleDefault: 'Search the knowledge base by topic',
    promptKey: 'chat.suggestedQuestions.findClause',
    promptDefault: SUGGESTED_QUESTIONS[1].defaultValue,
  },
  {
    titleKey: 'chat.suggestionCards.draftResponse.title',
    titleDefault: 'Draft a response',
    subtitleKey: 'chat.suggestionCards.draftResponse.subtitle',
    subtitleDefault: 'Use approved templates and prior answers',
    promptKey: 'chat.suggestedQuestions.draftEmail',
    promptDefault: SUGGESTED_QUESTIONS[5].defaultValue,
  },
  {
    titleKey: 'chat.suggestionCards.extractDates.title',
    titleDefault: 'Extract key dates',
    subtitleKey: 'chat.suggestionCards.extractDates.subtitle',
    subtitleDefault: 'Renewals, deadlines and due dates',
    promptKey: 'chat.suggestedQuestions.extractDates',
    promptDefault: SUGGESTED_QUESTIONS[8].defaultValue,
  },
  {
    titleKey: 'chat.suggestionCards.identifyRisks.title',
    titleDefault: 'Identify risks',
    subtitleKey: 'chat.suggestionCards.identifyRisks.subtitle',
    subtitleDefault: 'Flag risky or unusual clauses',
    promptKey: 'chat.suggestedQuestions.identifyRisks',
    promptDefault: SUGGESTED_QUESTIONS[6].defaultValue,
  },
  {
    titleKey: 'chat.suggestionCards.explainProcess.title',
    titleDefault: 'Explain a process',
    subtitleKey: 'chat.suggestionCards.explainProcess.subtitle',
    subtitleDefault: 'See how something works across your docs',
    promptKey: 'chat.suggestedQuestions.explainTopic',
    promptDefault: SUGGESTED_QUESTIONS[2].defaultValue,
  },
  {
    titleKey: 'chat.suggestionCards.listObligations.title',
    titleDefault: 'List obligations',
    subtitleKey: 'chat.suggestionCards.listObligations.subtitle',
    subtitleDefault: 'Obligations and who is accountable',
    promptKey: 'chat.suggestedQuestions.listObligations',
    promptDefault: SUGGESTED_QUESTIONS[4].defaultValue,
  },
  {
    titleKey: 'chat.suggestionCards.defineTerm.title',
    titleDefault: 'Define a term',
    subtitleKey: 'chat.suggestionCards.defineTerm.subtitle',
    subtitleDefault: 'Look up defined terms and meanings',
    promptKey: 'chat.suggestedQuestions.findDefinitions',
    promptDefault: SUGGESTED_QUESTIONS[7].defaultValue,
  },
];

/**
 * Returns `count` cards picked at random (Fisher-Yates, no mutation, SSR-safe — call inside a
 * component effect / useMemo on empty-screen mount so the pick is stable for that mount).
 * Falls back to the full list when `count` exceeds the pool. (#chat-random-suggestions)
 */
export const pickRandomCards = (count: number): SuggestionCard[] => {
  const pool = [...SUGGESTION_CARDS];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, Math.min(count, pool.length));
};

/**
 * Returns a new, shuffled copy of the provided list (Fisher-Yates).
 * Does not mutate the input and avoids module-level randomness (SSR-safe);
 * call this inside a component effect / useMemo on empty-screen mount.
 */
export const shuffleQuestions = (questions: SuggestedQuestion[]): SuggestedQuestion[] => {
  const result = [...questions];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};
